<template>
  <div class="flex flex-row self-center md:self-auto">
    <div class="relative">
      <button
        v-if="showSaveButton"
        :class="[
          'rounded-full p-6 py-2 text-white transition-all ',
          { 'is-loading': isSaving },
          { 'is-justify-content-start': saved && hasActiveOrder },
          {
            'bg-primary-2 dark:bg-primary-1 hover:dark:bg-primary-2':
              !errorStatus.hasError,
          },
          {
            'dark:bg-dark-error-0 hover:dark:bg-dark-error-0 bg-light-error/10 hover:bg-light-error':
              errorStatus.hasError,
          },
        ]"
        :disabled="isSaving"
        data-testid="uploadButton"
        @click="startSaveProcess"
      >
        <em
          :class="[
            'mdi leading-none',
            { 'mdi-check': saved },
            { 'mdi-loading mdi-spin': isSaving },
          ]"
        ></em>
        {{ saveButtonLabel }}
      </button>

      <div v-if="lastSaved && savedToday" class="mr-6 pt-1 text-right text-sm">
        zuletzt um {{ lastSavedTime }}
      </div>
      <ImageUploadHeader ref="imageUpload" class="mt-[-40px] w-full" />
    </div>
    <div>
      <button
        v-if="hasActiveOrder"
        :class="[
          'ml-3 rounded-full px-6 py-2 transition-all duration-300 ease-in-out',
          {
            'cursor-not-allowed bg-light-disabled-background dark:bg-dark-disabled-background':
              !saved,
          },
          {
            'bg-primary-2 text-white dark:bg-primary-1 hover:dark:bg-primary-2 ':
              saved,
          },
        ]"
        :disabled="!saved || !isOnline"
        data-testid="closeOrderBtn"
        @click="handleCloseOrderClick"
      >
        <span
          :class="[
            {
              'text-light-disabled-text dark:text-dark-disabled-text': !saved,
            },
          ]"
          >Auftrag abschließen</span
        >
      </button>
    </div>
  </div>
  <BasePrompt
    :open="funkcheckWarningOpen"
    icon="mdi-alert-circle text-red-500"
    title="Funkcheck nicht abgeschlossen"
    :question="`${funkcheckErrorCount} Gerät${funkcheckErrorCount === 1 ? '' : 'e'} ${funkcheckErrorCount === 1 ? 'hat' : 'haben'} kein Sendesignal. Der Auftrag kann trotzdem abgeschlossen werden.`"
    proceed-text="Trotzdem abschließen"
    cancel-text="Zurück"
    :close="() => (funkcheckWarningOpen = false)"
    :cancel="() => (funkcheckWarningOpen = false)"
    :proceed="onFunkcheckWarningProceed"
  />
  <BasePrompt
    :open="modalOpen"
    :close="toggleModal"
    :cancel="toggleModal"
    :proceed="onProceed"
    :icon="promptData.icon"
    :title="promptData.title"
    :question="promptData.question"
    :proceed-text="promptData.proceedText"
    :cancel-text="promptData.cancelText"
  >
    <template v-if="unfinishedSmartHeatingRoomGroups.size > 0">
      <div class="max-h-[50vh] overflow-y-auto px-4 py-2">
        <div class="space-y-2 text-left">
          <div
            v-for="roomGroup in unfinishedSmartHeatingRoomGroups.values()"
            :key="roomGroup.id"
            class="rounded-md bg-dark-2 px-3 py-2 dark:bg-dark-2"
          >
            <b>{{ getDisplayName(roomGroup) }}</b>
          </div>
        </div>
      </div>
    </template>

    <template v-if="isSmartHeatingOrderCompleted">
      <div class="mt-4 flex px-4 py-2">
        <em class="mdi mdi-information-outline text-primary-2"></em>
        <div class="ml-2 flex-1 text-left">
          Ein abgeschlossener Auftrag kann nicht nachträglich bearbeitet werden.
        </div>
      </div>
    </template>

    <template v-if="hasMissingLocationData">
      <div class="max-h-[50vh] overflow-y-auto px-4 py-2">
        <div class="space-y-2 text-left">
          <div
            v-for="ip in getLocationWithMissingData.values()"
            :key="ip.id"
            class="rounded-md bg-gray-100 px-3 py-2 dark:bg-gray-800"
          >
            <b>{{ getLocationDisplayName(ip) }}</b>
          </div>
        </div>
      </div>
    </template>
  </BasePrompt>
</template>

<script lang="ts" setup>
import { usePersistenceStore } from '@/store/persistence/persistenceStore';
import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { computed, ref } from 'vue';
import { useOrderStore } from '@/store/order/orderStore';
import { useLayoutStore, WorkMode } from '@/store/layout/layoutStore';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import ImageUploadHeader from '@/components/Header/ImageUploadHeader.vue';
import { useCloseOrderButton } from '@/composables/useCloseOrderButton';

const persistenceStore = usePersistenceStore();
const orderStore = useOrderStore();
const layoutStore = useLayoutStore();

const saved = computed(() => {
  return persistenceStore.saved;
});

const isSaving = computed(() => {
  return persistenceStore.isSaving.value;
});

const saveButtonLabel = computed(() => {
  return saved.value ? ' Daten gespeichert' : 'Daten speichern';
});

const lastSaved = computed(() => {
  return orderStore.activeOrder?.changes.lastSaved;
});

const savedToday = computed(() => {
  return orderStore.activeOrder?.changes.savedToday;
});

const lastSavedTime = computed(() => {
  return orderStore.activeOrder?.changes.lastSavedTime;
});

const errorStatus = computed(() => {
  return persistenceStore.errorStatus;
});

const hasActiveOrder = computed(() => {
  return !!orderStore.activeOrderId;
});

const showSaveButton = computed(() => {
  return layoutStore.workMode !== WorkMode.DEEP_LINK || hasActiveOrder.value;
});

const imageUpload = ref<null | { uploadImages: () => Promise<void> }>(null);

const funkcheckWarningOpen = ref(false);

const funkcheckErrorCount = computed(() => {
  const statuses = useDmgw6Store().deviceFunkcheckStatuses;
  return [...statuses.values()].filter(
    (s) => s === Dmgw6DeviceFunkcheckStatus.error
  ).length;
});

const handleCloseOrderClick = () => {
  const hasDmgw6 = !!useDeviceStore().getDirectMeterGateway();
  if (hasDmgw6 && funkcheckErrorCount.value > 0) {
    funkcheckWarningOpen.value = true;
  } else {
    openDialog();
  }
};

const onFunkcheckWarningProceed = () => {
  funkcheckWarningOpen.value = false;
  openDialog();
};

const {
  isOnline,
  modalOpen,
  startSaveProcess,
  toggleModal,
  unfinishedSmartHeatingRoomGroups,
  isSmartHeatingOrderCompleted,
  hasMissingLocationData,
  promptData,
  getLocationWithMissingData,
  openDialog,
  onProceed,
  getDisplayName,
  getLocationDisplayName,
} = useCloseOrderButton();
</script>
